import React, { useCallback, useState } from 'react';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { FilterOp, SearchResult } from '@marageti/z4-sdk/lib/entity';
import { Ticket } from '@marageti/z4-sdk/lib/tickets';
import useTicketSummaryActions from '../../hooks/use-ticket-summary-actions';
import TicketEditModal from '../modals/ticket-edit-modal';
import TicketsSummaryView from './tickets-summary-view';

const PAGE_SIZE = 5;

type TicketsSummaryContainerProps = {
  tripRequestId?: string;
  travelerId?: string;
  travelerName?: string;
  travelerEmail?: string;
  emptyMessage?: string;
  refetchTicketCount?: () => void;
};

const TicketsSummaryContainer = ({
  tripRequestId,
  travelerId,
  travelerName,
  travelerEmail,
  emptyMessage,
  refetchTicketCount,
}: TicketsSummaryContainerProps) => {
  const apiClient = useApiClient();
  const [limit, setLimit] = useState(PAGE_SIZE);

  const { data, isLoading, mutate } = useFetch<SearchResult<Ticket>>(
    apiClient.ticketsClient.searchTickets,
    [{
      filterBy: [
        { field: 'tripRequestId', value: [tripRequestId || ''] },
        { field: 'ticketType', value: ['ISSUE'] },
        { field: 'status', op: FilterOp.FilterOpNot, value: ['CANCELED'] },
      ],
      sortBy: [{ field: 'createdAt', order: 'desc' }],
      pagination: { limit, page: 1 },
    }],
    !!tripRequestId,
  );

  const tickets = data?.hits || [];
  const hasMore = (data?.totalHits || 0) > tickets.length;

  const handleSeeMore = useCallback(() => {
    setLimit((prev) => prev + PAGE_SIZE);
  }, []);

  const {
    create,
    editModalProps,
  } = useTicketSummaryActions({
    refetch: mutate,
    refetchTicketCount,
    tripRequestId,
    travelerId,
    travelerName,
    travelerEmail,
  });

  return (
    <>
      <TicketsSummaryView
        tickets={tickets}
        isLoading={isLoading}
        hasMore={hasMore}
        emptyMessage={emptyMessage}
        onAddTicket={create}
        onSeeMore={handleSeeMore}
      />

      <TicketEditModal
        open={editModalProps.open}
        onClose={editModalProps.onClose}
        onSave={editModalProps.onSave}
        assignees={editModalProps.assignees}
        defaultValues={editModalProps.defaultValues}
        isNew={editModalProps.isNew}
      />
    </>
  );
};

export default TicketsSummaryContainer;
